import React, { useRef, useState } from "react";

import ShareButton from "../share-button/share-button";
import HeaderButton from "../header-button/header-button";

import linkedInIcon from "../../assets/img/linkedIn-filled.svg";
import twitterIcon from "../../assets/img/twitter-logo.svg";
import mailIcon from "../../assets/img/mail.svg";
import linkIcon from "../../assets/img/link.svg";
import "./card.scss";
import {
  getShareSocialNetworkLink,
  getTextEllipse,
} from "../../core/utils/functions";
import { FavoriteButton } from "../favorite-button/favorite-button";

export function Card({
  item,
  onReadMore,
  onRefreshItems,
  isShowHeaderButton = true,
  isShowFooterButton = true,
  isShortDescription = true,
  labels,
  coverImage = false,
}) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [isReadingOn, setIsReadingOn] = useState(false);
  const [isClipboardCopying, setIsClipboardCopying] = useState(false);

  const shareLink = useRef(getShareLink()).current;
  const { twitterURL, mailtoURL, linkedinURL } = useRef(
    getShareSocialNetworkLink(shareLink)
  ).current;

  function toggleSharingIconsVisibility() {
    if (!isReadingOn) setIsSharing((prevState) => !prevState);
  }

  function toggleReadingIconsVisibility() {
    if (!isSharing) setIsReadingOn((prevState) => !prevState);
  }

  function handleOnLoad() {
    setIsLoaded(true);
  }

  function handleIsClipboardCopy() {
    setIsClipboardCopying(true);
    toggleSharingIconsVisibility();
  }

  function getShareLink() {
    let id = item?.id;
    let slug = item?.slug;
    let absolutePath = window.location.href;
    return `${absolutePath}/${id}-${slug}`;
  }

  const Skeleton = () => (
    <>
      <article className="wrapper___card__wrapper">
        <div className="wrapper___skeleton ">
          <header className="wrapper___skeleton-header" />
          <div className="wrapper___skeleton-body">
            <span />
          </div>
          <div className="wrapper___skeleton-footer">
            <span className="wrapper___skeleton-read-more" />
            {isShowFooterButton && (
              <div className="wrapper___skeleton-icons-wrapper">
                <span />
                <span />
              </div>
            )}
          </div>
        </div>
      </article>
    </>
  );

  return (
    <>
      {item ? (
        <article className="wrapper___card__wrapper">
          {/* HEADER */}
          <header className="wrapper___card__header">
            <div
              className={`wrapper___skeleton-header 
            wrapper___card__overlay ${
              (isSharing || isReadingOn) && "overlay-visible"
            }`}
            >
              {/* PICTURE */}
              <div
                className="wrapper___card__image_container"
                onClick={() => onReadMore?.(item.id, item.slug)}
              >
                <img
                  className={`wrapper___card__image thumb ${
                    !!isLoaded && "isLoaded"
                  }`}
                  src={
                    item?.thumbnail_video?.url ||
                    item?.image?.formats?.thumbnail?.url
                  }
                  alt="Thumbnail"
                />
                <img
                  className={`wrapper___card__image ${
                    !!isLoaded && "isLoaded"
                  }`}
                  style={{
                    objectFit: coverImage ? "fill" : "contain",
                  }}
                  src={
                    item?.thumbnail_video?.url ||
                    item?.image?.formats?.small?.url ||
                    item?.image?.formats?.thumbnail?.url ||
                    item?.image?.url
                  }
                  alt="Card"
                  onLoad={handleOnLoad}
                />
              </div>

              {/*OVERLAY SHARE BUTTONS*/}
              <div className="wrapper___card__overlay-content">
                <h2>{isReadingOn ? "See on" : labels?.share}</h2>
                <div className="card__overlay_button_wrapper">
                  {isReadingOn && !isSharing
                    ? item?.open_in_options.map((option, index) => {
                        const {
                          icon: {
                            icon: { url, alternativeText },
                          },
                          link,
                        } = option;
                        return (
                          <a
                            key={link}
                            className="card__overlay-button"
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={toggleReadingIconsVisibility}
                          >
                            <img
                              key={url + index}
                              src={url}
                              alt={alternativeText}
                              className="header-button-icon__svg"
                            />
                          </a>
                        );
                      })
                    : null}
                  {isSharing && !isReadingOn && (
                    <>
                      {/* LINKEDIN BUTTON */}
                      <a
                        className="card__overlay-button"
                        href={linkedinURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={toggleSharingIconsVisibility}
                      >
                        <img src={linkedInIcon} alt="Share on LinkedIn" />
                      </a>
                      {/* TWITTER BUTTON */}
                      <a
                        className="card__overlay-button"
                        href={twitterURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={toggleSharingIconsVisibility}
                      >
                        <img src={twitterIcon} alt="Share on Twitter" />
                      </a>
                      {/* EMAIL BUTTON */}
                      <a
                        className="card__overlay-button"
                        href={mailtoURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={toggleSharingIconsVisibility}
                      >
                        <img src={mailIcon} alt="Share through email" />
                      </a>
                      {/* COPY LINK BUTTON */}
                      <button
                        className="card__overlay-button"
                        onClick={handleIsClipboardCopy}
                      >
                        <img src={linkIcon} alt="Copy link to clipboard" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
            {/*CARD HEADER BUTTON*/}
            {isShowHeaderButton && item?.open_in_options ? (
              <HeaderButton
                isOpen={isReadingOn}
                options={item?.open_in_options}
                onClick={toggleReadingIconsVisibility}
              />
            ) : null}
          </header>
          {/* TITLE */}
          <p
            className="wrapper___card__title"
            dangerouslySetInnerHTML={{
              __html: getTextEllipse(
                item?.title,
                isShortDescription ? 180 : 0,
                true
              ),
            }}
          />
          {/* DESCRIPTION */}
          <p
            className="wrapper___card__body"
            dangerouslySetInnerHTML={{
              __html: getTextEllipse(
                item?.body || item?.blurb,
                isShortDescription ? 180 : 0,
                true
              ),
            }}
          />
          {/*FOOTER*/}
          <div className="wrapper___card__footer">
            {/*READ MORE*/}
            <span
              onClick={() => onReadMore && onReadMore(item.id, item.slug)}
              className="wrapper___card__read-more"
            >
              {labels.readMore}
            </span>
            {/* SOCIAL ICONS*/}
            {isShowFooterButton && (
              <div className="wrapper___card__icons-wrapper">
                {/* SHARE ICON */}
                <ShareButton
                  isShowTitle={false}
                  type={"SHARE"}
                  label={labels.share}
                  successMsg={labels.successCopied}
                  shareLink={shareLink}
                  copyLink={shareLink}
                  isOpen={isSharing || isClipboardCopying}
                  isTriggerCopied={isClipboardCopying}
                  onClick={toggleSharingIconsVisibility}
                  onHideCopiedMessage={() => setIsClipboardCopying(false)}
                />
                {/* FAVORITE ICON*/}
                <FavoriteButton
                  itemId={item?.id}
                  favoriteId={item?.favorite}
                  onRefreshItems={onRefreshItems}
                />
              </div>
            )}
          </div>
        </article>
      ) : (
        <Skeleton />
      )}
    </>
  );
}
